import { useQuery } from "@tanstack/react-query";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type SourceMeta } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  BarList,
  EmptyState,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

function SourceRight({ meta }: { meta?: SourceMeta }) {
  if (!meta?.available) return null;
  return (
    <span className="flex items-center gap-2">
      <StatusPill status={meta.status ?? "unknown"} />
      <span className="text-xs text-slate-500">{fmtRelative(meta.captured_at)}</span>
    </span>
  );
}

export default function WorkforceCapacityPage() {
  const q = useQuery({ queryKey: ["workforce-capacity"], queryFn: dashApi.workforceCapacity });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workforce & Capacity"
        description="IronRota coverage with a per-company split, plus a whole-business capacity-pressure view. No personal, salary or identity data is read."
        right={<SourceRight meta={d?.source} />}
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d || !d.source.available ? (
        <EmptyState>No IronRota snapshot has been captured yet.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Uncovered shifts next 7d" value={d.rota.coverage_gaps_next7} unit="count" tone={d.rota.coverage_gaps_next7 ? "danger" : undefined} />
            <KpiCard label="Shifts published" value={d.rota.shifts_published} unit="count" />
            <KpiCard label="Staff scheduled" value={d.rota.staff_scheduled} unit="count" />
            <KpiCard label="Planned hours" value={d.rota.hours?.planned ?? null} unit="count" />
            <KpiCard label="Worked hours" value={d.rota.hours?.worked ?? null} unit="count" />
            <KpiCard label="Leave pending" value={d.rota.leave_pending} unit="count" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="By company"
              subtitle="BGR FIX IT CANARIES staff (C001-C010) are disposable trial rows — headline numbers blend both companies; this split is the honest view."
            >
              {d.rota.by_company.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Company</th>
                      <th className="pb-2 text-right">Active staff</th>
                      <th className="pb-2 text-right">Shifts (range)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rota.by_company.map((c) => (
                      <tr key={c.name} className="border-t border-slate-800">
                        <td className="py-2 text-slate-300">{c.name}</td>
                        <td className="py-2 text-right text-slate-200">{c.active_staff}</td>
                        <td className="py-2 text-right text-slate-200">{c.shifts_published}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState compact>No companies found.</EmptyState>
              )}
            </Panel>
            <Panel
              title="Hours: planned vs worked"
              subtitle={d.rota.hours?.coverage_pct != null ? `Coverage ${d.rota.hours.coverage_pct}% of planned hours` : "Attendance adoption is still low — worked hours may be near zero."}
            >
              <BarList
                rows={[
                  { label: "Planned", value: d.rota.hours?.planned ?? 0 },
                  { label: "Worked", value: d.rota.hours?.worked ?? 0 },
                ]}
                unit="count"
              />
            </Panel>
            <Panel title="Approved leave overlapping range" subtitle={`Oldest pending approval: ${d.rota.leave_pending_oldest_days ?? 0} day(s)`}>
              <BarList rows={d.rota.leave_by_type} unit="count" empty="No approved leave overlaps this range." />
            </Panel>
            <Panel title="Branch coverage (shifts in range)">
              <BarList
                rows={d.rota.branch_coverage.map((b) => ({ label: `${b.name} (${b.code})`, value: b.shifts }))}
                unit="count"
                empty="No branch shifts in range."
              />
            </Panel>
          </div>

          <Panel title="Capacity pressure (whole business)" subtitle={d.pressure.note}>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              <KpiCard label="Open repairs" value={d.pressure.repair_open_tickets} unit="count" />
              <KpiCard label="Repairs unassigned" value={d.pressure.repair_unassigned} unit="count" />
              <KpiCard label="Support awaiting reply" value={d.pressure.support_waiting_reply} unit="count" />
              <KpiCard label="Support unassigned" value={d.pressure.support_unassigned} unit="count" />
              <KpiCard label="Staff scheduled" value={d.pressure.staff_scheduled} unit="count" />
            </div>
          </Panel>

          <GapList gaps={d.source.gaps} />
          <FeedbackWidget targetType="page" targetRef="/workforce/capacity" />
        </>
      )}
    </div>
  );
}
